/**
 * Rosie — NEMT Dispatch Platform
 * Copyright (c) 2025-2026 Commonwealth Transport LLC. All rights reserved.
 * PROPRIETARY AND CONFIDENTIAL. Unauthorized use, copying, or distribution is prohibited.
 */
import React, { useState } from 'react';
import api from '../utils/api';

function parseCSV(text) {
  const lines = text.split('\n');
  const rows = [];
  // Skip header rows (first 2 lines)
  for (let i = 2; i < lines.length; i++) {
    const line = lines[i].trim();
    if (!line) continue;
    const fields = [];
    let field = '';
    let inQuotes = false;
    for (let c = 0; c < line.length; c++) {
      if (line[c] === '"') { inQuotes = !inQuotes; }
      else if (line[c] === ',' && !inQuotes) { fields.push(field.trim()); field = ''; }
      else { field += line[c]; }
    }
    fields.push(field.trim());

    // Normalize date (handle "4/20/2026" → "2026-04-20")
    let rawDate = fields[2] || '';
    const dp = rawDate.split('/');
    let date = rawDate;
    if (dp.length === 3) {
      const yr = dp[2].length === 4 ? dp[2] : '2026';
      date = `${yr}-${dp[0].padStart(2,'0')}-${dp[1].padStart(2,'0')}`;
    }

    rows.push({
      timestamp: fields[0],
      facility: fields[1],
      date,
      patient: fields[3],
      mrn: fields[4],
      startTime: fields[5],
      pickupTime: fields[6],
      dropoffTime: fields[7],
      returnTime: fields[8],
      totalHours: parseFloat(fields[9]) || 0,
      odomStart: parseInt(fields[10]) || 0,
      odomStop: parseInt(fields[11]) || 0,
      mileage: parseInt(fields[12]) || 0,
      from: fields[13],
      to: fields[14],
      vehicle: fields[15],
      tolls: fields[16],
      stops: fields[17],
      mealRequested: fields[18],
      meal1: parseFloat(fields[19]) || 0,
      meal2: parseFloat(fields[20]) || 0,
      transporter1: fields[21],
      transporter2: fields[22],
      additionalTransporters: fields[23],
      cancelled: fields[24],
      cleanupFee: fields[25],
      notes: fields[26],
    });
  }
  return rows;
}

const BADGE = {
  match: { bg: '#E8F5E9', color: '#2E7D32', label: 'Match' },
  mismatch: { bg: '#FFF3E0', color: '#E65100', label: 'Mismatch' },
  missing: { bg: '#FFEBEE', color: '#C62828', label: 'Missing' },
};

export default function SheetsCompare() {
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all'); // all, mismatches, unmatched
  const [expandedIdx, setExpandedIdx] = useState(null);

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setError('');
    setLoading(true);
    try {
      const text = await file.text();
      const rows = parseCSV(text);
      if (rows.length === 0) { setError('No data found in CSV'); setLoading(false); return; }
      const res = await api.post('/api/billing/compare-sheets', { rows });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
    setLoading(false);
  };

  const s = result?.summary;

  const filteredComparisons = result?.comparisons?.filter(c => {
    if (filter === 'mismatches') return c.hasDiscrepancy;
    return true;
  }) || [];

  return (
    <div style={{ padding: '20px', maxWidth: 1200, margin: '0 auto' }}>
      <h2 style={{ color: '#185FA5', marginBottom: 4 }}>Google Sheets Comparison</h2>
      <p style={{ color: '#888', fontSize: 13, marginBottom: 16 }}>
        Upload your weekly Google Sheets CSV export to compare against Rosie's records
      </p>

      <div style={{ marginBottom: 20 }}>
        <label style={{
          display: 'inline-block', padding: '10px 24px', background: '#185FA5', color: '#fff',
          borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600
        }}>
          {loading ? 'Comparing...' : 'Upload CSV'}
          <input type="file" accept=".csv" onChange={handleFile} style={{ display: 'none' }} disabled={loading} />
        </label>
      </div>

      {error && <div style={{ padding: 12, background: '#FFF3E0', color: '#E65100', borderRadius: 8, marginBottom: 16, fontSize: 13 }}>{error}</div>}

      {s && (
        <>
          {/* Summary cards */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
            <Card label="Sheets Trips" value={s.sheetsPerformed} sub={`+ ${s.sheetsCancelled} cancelled`} />
            <Card label="Rosie Trips" value={s.rosieTotal} />
            <Card label="Matched" value={s.matched} color="#2E7D32" />
            <Card label="Discrepancies" value={s.discrepancies} color={s.discrepancies > 0 ? '#E65100' : '#2E7D32'} />
            <Card label="Sheets Hours" value={s.totalSheetHours} />
            <Card label="Rosie Hours" value={s.totalRosieHours} color={Math.abs(s.totalSheetHours - s.totalRosieHours) > 1 ? '#E65100' : undefined} />
            <Card label="Sheets Miles" value={s.totalSheetMiles.toLocaleString()} />
            <Card label="Rosie Miles" value={Math.round(s.totalRosieMiles).toLocaleString()} color={Math.abs(s.totalSheetMiles - s.totalRosieMiles) > 10 ? '#E65100' : undefined} />
            <Card label="Unmatched (Sheets)" value={result.unmatchedSheets?.length || 0} color={(result.unmatchedSheets?.length || 0) > 0 ? '#C62828' : undefined} />
            <Card label="Unmatched (Rosie)" value={result.unmatchedRosie?.length || 0} color={(result.unmatchedRosie?.length || 0) > 0 ? '#C62828' : undefined} />
          </div>

          {/* Driver hours comparison */}
          {result.sheetDriverHours && (
            <div style={{ marginBottom: 20 }}>
              <h3 style={{ fontSize: 14, color: '#185FA5', marginBottom: 8 }}>Driver Hours: Sheets vs Rosie</h3>
              <div style={{ background: '#f9f9f9', borderRadius: 8, padding: 12, fontSize: 12, overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '2px solid #ddd' }}>
                      <th style={{ textAlign: 'left', padding: '4px 8px' }}>Driver</th>
                      <th style={{ textAlign: 'right', padding: '4px 8px' }}>Sheets Hours</th>
                      <th style={{ textAlign: 'right', padding: '4px 8px' }}>Rosie Hours</th>
                      <th style={{ textAlign: 'right', padding: '4px 8px' }}>Diff</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(result.sheetDriverHours).sort((a,b) => b[1] - a[1]).map(([name, hrs]) => {
                      // Find this driver in Rosie comparisons
                      let rosieHrs = 0;
                      const firstName = name.split(' ')[0];
                      for (const c of result.comparisons) {
                        for (const a of (c.rosieTrip.assignees || [])) {
                          if (a.first_name === firstName) rosieHrs += parseFloat(a.billable_hours || 0);
                        }
                      }
                      const diff = hrs - rosieHrs;
                      const diffColor = Math.abs(diff) < 0.5 ? '#2E7D32' : '#E65100';
                      return (
                        <tr key={name} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '4px 8px', fontWeight: 500 }}>{name}</td>
                          <td style={{ textAlign: 'right', padding: '4px 8px' }}>{hrs.toFixed(2)}</td>
                          <td style={{ textAlign: 'right', padding: '4px 8px' }}>{rosieHrs.toFixed(2)}</td>
                          <td style={{ textAlign: 'right', padding: '4px 8px', color: diffColor, fontWeight: 600 }}>
                            {diff > 0 ? '+' : ''}{diff.toFixed(2)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Filter tabs */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
            {[
              ['all', `All (${result.comparisons?.length || 0})`],
              ['mismatches', `Mismatches (${s.discrepancies})`],
              ['unmatched', `Unmatched`],
            ].map(([key, label]) => (
              <button key={key} onClick={() => setFilter(key)} style={{
                padding: '6px 14px', borderRadius: 6, border: 'none', fontSize: 12, fontWeight: 600,
                background: filter === key ? '#185FA5' : '#e9e9e9', color: filter === key ? '#fff' : '#333',
                cursor: 'pointer',
              }}>{label}</button>
            ))}
          </div>

          {/* Trip-by-trip comparisons */}
          {filter !== 'unmatched' && filteredComparisons.map((c, i) => {
            const badge = c.hasDiscrepancy ? BADGE.mismatch : BADGE.match;
            const expanded = expandedIdx === i;
            return (
              <div key={i} onClick={() => setExpandedIdx(expanded ? null : i)} style={{
                background: '#fff', border: `1px solid ${c.hasDiscrepancy ? '#FFB74D' : '#C8E6C9'}`,
                borderRadius: 8, padding: '10px 14px', marginBottom: 8, cursor: 'pointer',
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ background: badge.bg, color: badge.color, padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 }}>{badge.label}</span>
                    <span style={{ fontWeight: 600, fontSize: 13 }}>{c.sheetRow.patient}</span>
                    <span style={{ color: '#888', fontSize: 12 }}>{c.sheetRow.date}</span>
                    {c.rosieTrip.transport_number && <span style={{ color: '#185FA5', fontSize: 11 }}>{c.rosieTrip.transport_number}</span>}
                  </div>
                  <div style={{ display: 'flex', gap: 12, fontSize: 12, color: '#555' }}>
                    <span>{c.sheetRow.totalHours}h</span>
                    <span>{c.sheetRow.mileage}mi</span>
                    <span>{c.sheetRow.transporter1}{c.sheetRow.transporter2 ? ` + ${c.sheetRow.transporter2}` : ''}</span>
                  </div>
                </div>

                {c.hasDiscrepancy && (
                  <div style={{ marginTop: 6, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {c.discrepancies.map((d, j) => (
                      <span key={j} style={{ fontSize: 11, background: '#FFF3E0', padding: '2px 8px', borderRadius: 4 }}>
                        <b>{d.field}:</b> Sheets={typeof d.sheets === 'number' ? d.sheets.toFixed?.(2) ?? d.sheets : d.sheets} vs Rosie={typeof d.rosie === 'number' ? d.rosie.toFixed?.(2) ?? d.rosie : d.rosie}
                      </span>
                    ))}
                  </div>
                )}

                {expanded && (
                  <div style={{ marginTop: 10, fontSize: 12, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <div style={{ fontWeight: 600, color: '#185FA5', marginBottom: 4 }}>Google Sheets</div>
                      <div><b>From:</b> {c.sheetRow.from}</div>
                      <div><b>To:</b> {c.sheetRow.to}</div>
                      <div><b>Vehicle:</b> {c.sheetRow.vehicle}</div>
                      <div><b>Hours:</b> {c.sheetRow.totalHours}</div>
                      <div><b>Miles:</b> {c.sheetRow.mileage} (odom: {c.sheetRow.odomStart} → {c.sheetRow.odomStop})</div>
                      <div><b>Times:</b> {c.sheetRow.startTime} → {c.sheetRow.returnTime}</div>
                      <div><b>Drivers:</b> {c.sheetRow.transporter1}{c.sheetRow.transporter2 ? ', ' + c.sheetRow.transporter2 : ''}</div>
                      <div><b>Tolls:</b> {c.sheetRow.tolls}</div>
                      <div><b>Meals:</b> ${(c.sheetRow.meal1 + c.sheetRow.meal2).toFixed(2)}</div>
                      {c.sheetRow.notes && <div><b>Notes:</b> {c.sheetRow.notes}</div>}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, color: '#0F6E56', marginBottom: 4 }}>Rosie</div>
                      <div><b>From:</b> {c.rosieTrip.origin}</div>
                      <div><b>To:</b> {c.rosieTrip.destination}</div>
                      <div><b>Vehicle:</b> {c.rosieTrip.vehicle}</div>
                      <div><b>Hours:</b> {c.rosieTrip.hours}</div>
                      <div><b>Miles:</b> {c.rosieTrip.miles}</div>
                      <div><b>Status:</b> {c.rosieTrip.status}</div>
                      <div><b>Drivers:</b> {c.rosieTrip.assignees?.map(a => a.driver_name).join(', ')}</div>
                      <div><b>Billing:</b> ${c.rosieTrip.billing_total?.toFixed(2)}</div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Unmatched sections */}
          {filter === 'unmatched' && (
            <>
              {result.unmatchedSheets?.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <h3 style={{ fontSize: 14, color: '#C62828', marginBottom: 8 }}>
                    In Google Sheets but NOT in Rosie ({result.unmatchedSheets.length})
                  </h3>
                  {result.unmatchedSheets.map((r, i) => (
                    <div key={i} style={{ background: '#FFEBEE', borderRadius: 8, padding: '8px 14px', marginBottom: 6, fontSize: 12 }}>
                      <b>{r.patient}</b> — {r.date} — {r.from} → {r.to} — {r.totalHours}h, {r.mileage}mi — {r.transporter1}
                      {r.cancelled?.includes('Cancelled') && <span style={{ color: '#C62828', fontWeight: 600 }}> (CANCELLED)</span>}
                    </div>
                  ))}
                </div>
              )}
              {result.unmatchedRosie?.length > 0 && (
                <div>
                  <h3 style={{ fontSize: 14, color: '#C62828', marginBottom: 8 }}>
                    In Rosie but NOT in Google Sheets ({result.unmatchedRosie.length})
                  </h3>
                  {result.unmatchedRosie.map((r, i) => (
                    <div key={i} style={{ background: '#FFF3E0', borderRadius: 8, padding: '8px 14px', marginBottom: 6, fontSize: 12 }}>
                      <b>{r.transport_number}</b> — {r.patient_name} — {String(r.date).substring(0, 10)} — {r.destination} — {r.status}
                    </div>
                  ))}
                </div>
              )}
              {!result.unmatchedSheets?.length && !result.unmatchedRosie?.length && (
                <p style={{ color: '#2E7D32', fontSize: 13 }}>All trips matched between Sheets and Rosie.</p>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function Card({ label, value, sub, color }) {
  return (
    <div style={{ background: '#f9f9f9', borderRadius: 8, padding: '10px 14px', textAlign: 'center' }}>
      <div style={{ fontSize: 11, color: '#888', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: color || '#333' }}>{value}</div>
      {sub && <div style={{ fontSize: 10, color: '#aaa' }}>{sub}</div>}
    </div>
  );
}
